import "../../style.css";

import React, { useCallback, useEffect, useState } from "react";

import Animations from "../../Animations";

const Gallery = ({ slug }) => {
  const isInViewport = (imageId) => {
    const imageElement = document.getElementById(`item${imageId}`);
    if (!imageElement) return false;
    const rect = imageElement.getBoundingClientRect();
    return (
      rect.bottom >= 0 &&
      rect.right >= 0 &&
      rect.top <=
        (window.innerHeight || document.documentElement.clientHeight) &&
      rect.left <= (window.innerWidth || document.documentElement.clientWidth)
    );
  };

  // Combined logic for loading JPG and PNG images
  const loadImage = (imageId, extension = "jpg") => {
    const imageDirectory = getImageDirectory();
    const imageUrl = `${imageDirectory}${slug}/image${imageId}.${extension}`;
    const img = new Image();
    img.src = imageUrl;
    img.onload = () => {
      setImageList((currentList) =>
        currentList.map((image) => {
          if (image.id === imageId) {
            return { ...image, loaded: true, loading: false };
          }
          return image;
        })
      );
    };
    img.onerror = () => {
      if (extension === "jpg") {
        loadImage(imageId, "png"); // Try PNG if JPG fails
      } else {
        setImageList((currentList) =>
          currentList.map((image) => {
            if (image.id === imageId) {
              return { ...image, failed: true, loading: false };
            }
            return image;
          })
        );
      }
    };
  };

  const [imageList, setImageList] = useState([]);
  const [activeImage, setActiveImage] = useState(null);
  const [galleryDetails, setGalleryDetails] = useState({});
  const [galleryExists, setGalleryExists] = useState(true);
  const [showSwipeText, setShowSwipeText] = useState(false);
  const [instructionsShown, setInstructionsShown] = useState(false);
  const [templateName, setTemplateName] = useState("");
  const [isLoading, setIsLoading] = useState(true); // New state to track if images are loading

  // Add state for tracking touch positions
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);

  // Minimum distance (in pixels) to detect a swipe
  const minSwipeDistance = 50;

  // Event handlers for touch events
  const handleTouchStart = (event) => {
    setTouchEnd(null); // Reset touch end position
    setTouchStart(event.targetTouches[0].clientX);
  };

  const handleTouchMove = (event) => {
    setTouchEnd(event.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    // Determine if a swipe was made and update the active image
    if (isLeftSwipe) {
      handleArrowClick("right");
    } else if (isRightSwipe) {
      handleArrowClick("left");
    }
  };

  useEffect(() => {
    const checkImagesOnScroll = () => {
      imageList.forEach((image) => {
        if (!image.loaded && isInViewport(image.id)) {
          loadImage(image.id);
        }
      });
    };

    window.addEventListener("scroll", checkImagesOnScroll);
    return () => window.removeEventListener("scroll", checkImagesOnScroll);
  }, [imageList]);

  useEffect(() => {
    const pathParts = window.location.pathname.split("/");
    const updatedTemplateName = pathParts[2];
    setTemplateName(updatedTemplateName);
  }, []);

  // Determine the image directory based on the template name
  const getImageDirectory = useCallback(() => {
    switch (templateName) {
      case "gallery":
        return `/assets/images/portfolio/gallery/gallery/`;
      case "generative":
        return `/assets/images/portfolio/gallery/generative/`;
      default:
        return `/assets/images/portfolio/gallery/`;
    }
  }, [templateName]);

  useEffect(() => {
    const checkImagesOnScroll = () => {
      imageList.forEach((image) => {
        if (!image.loaded && isInViewport(image.id)) {
          loadImage(image.id);
        }
      });
    };

    window.addEventListener("scroll", checkImagesOnScroll);
    return () => window.removeEventListener("scroll", checkImagesOnScroll);
  }, [imageList]);

  useEffect(() => {
    let isMounted = true;

    const fetchGalleryDetails = async () => {
      try {
        const response = await fetch(
          `/assets/portfoliopages/${templateName}/${slug}-gallery.json`
        );

        // Check if response is OK and if it's JSON
        if (response.ok) {
          const contentType = response.headers.get("content-type");
          if (contentType && contentType.includes("application/json")) {
            const json = await response.json();
            if (json && json.length > 0 && isMounted) {
              setGalleryDetails(json[0]);
              setGalleryExists(true);
            } else if (isMounted) {
              setGalleryExists(false);
            }
          } else {
            if (isMounted) {
              // console.error("Received non-JSON response");
              setGalleryExists(false);
            }
          }
        } else {
          if (isMounted) {
            // console.error(`HTTP status ${response.status}`);
            setGalleryExists(false);
          }
        }
      } catch (error) {
        // console.error(`An error occurred: ${error}`);
        if (isMounted) setGalleryExists(false);
      }
    };

    fetchGalleryDetails();

    return () => (isMounted = false);
  }, [slug, templateName]);

  useEffect(() => {
    const checkImagesOnScroll = () => {
      imageList.forEach((image) => {
        if (!image.loaded && isInViewport(image.id)) {
          loadImage(image.id);
        }
      });
    };

    window.addEventListener("scroll", checkImagesOnScroll);
    return () => window.removeEventListener("scroll", checkImagesOnScroll);
  }, [imageList]);

  useEffect(() => {
    if (!galleryExists || Object.keys(galleryDetails).length === 0) {
      return;
    }

    const imageDirectory = getImageDirectory();
    const list = [];
    let id = 1;
    let loadingCount = 0;
    let normalImageCount = 0;
    let heroImageCount = 0;

    const loadImage = (imageId) => {
      const load = (extension) => {
        const imageUrl = `${imageDirectory}${slug}/image${imageId}.${extension}`;
        const img = new Image();
        img.src = imageUrl;
        loadingCount++;

        img.onload = () => {
          const aspectRatio = img.width / img.height;
          let className = "";

          // Logic to determine if the image should be a hero
          if (templateName === "gallery" || templateName === "generative") {
            if (normalImageCount >= 3 && Math.random() < 0.5) {
              // Random chance to make an image a hero after 3 normal images
              className = "item--hero";
              heroImageCount++;
              normalImageCount = 0;
            } else {
              normalImageCount++;
              heroImageCount = 0;
            }

            if (heroImageCount > 2) {
              // Ensure no more than 3 consecutive heroes
              className = "";
              heroImageCount = 0;
              normalImageCount = 1;
            }
          }

          // Existing logic for other image types
          if (className === "") {
            if (aspectRatio > 1.2 && aspectRatio < 1.7777) {
              className = "item--medium";
            } else if (aspectRatio < 0.8) {
              className = "item--large";
            } else if (aspectRatio >= 1.7778 && aspectRatio <= 3.5556) {
              className = "item--full";
            }
          }

          list.push({
            id: imageId,
            details:
              galleryDetails[imageId.toString()] || "No details available",
            imageUrl,
            className,
          });

          if (--loadingCount === 0) {
            setIsLoading(false);
            setImageList([...list].sort((a, b) => a.id - b.id));
          }
          loadImage(imageId + 1);
        };

        img.onerror = () => {
          if (--loadingCount === 0) {
            setIsLoading(false);
            setImageList([...list].sort((a, b) => a.id - b.id));
          }
          if (extension === "jpg") {
            load("png");
          }
        };
      };

      load("jpg");
    };

    loadImage(id);
  }, [getImageDirectory, galleryDetails, slug, galleryExists, templateName]);

  const handleImageClick = (id, event) => {
    event.stopPropagation();
    setActiveImage(id);
    document.body.style.overflow = "hidden";
    // Move focus to the overlay
    setTimeout(() => {
      document.querySelector(".overlay").focus();
    }, 0);
  };

  const handleThumbnailKeyDown = (id, event) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault(); // Prevent any default actions
      handleImageClick(id, event);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleCloseClick = useCallback(() => {
    setActiveImage(null);
    document.body.style.overflow = "";

    const thumbnailElement = document.querySelector(`#item${activeImage}`);
    if (thumbnailElement) {
      thumbnailElement.focus();
    }
  }, [activeImage]);

  const handleArrowClick = (direction) => {
    const currentIndex = imageList.findIndex(({ id }) => id === activeImage);
    if (direction === "left" && currentIndex > 0) {
      setActiveImage(imageList[currentIndex - 1].id);
    } else if (direction === "right" && currentIndex < imageList.length - 1) {
      setActiveImage(imageList[currentIndex + 1].id);
    }
  };

  const [lastKeyTime, setLastKeyTime] = useState(0);

  const handleKeyDown = useCallback(
    (event) => {
      const currentTime = new Date().getTime();
      if (currentTime - lastKeyTime < 200) {
        // Ignore this event, as another key event happened within the last 200ms
        return;
      }
      setLastKeyTime(currentTime);
      const focusedElement = document.activeElement;
      const currentIndex = imageList.findIndex(({ id }) => id === activeImage);

      if (focusedElement && focusedElement.classList.contains("overlay")) {
        // An image is active and focus is on the overlay
        if (event.key === "Enter") {
          handleCloseClick();
        } else if (event.key === " ") {
          event.preventDefault();
          if (currentIndex < imageList.length - 1) {
            setActiveImage(imageList[currentIndex + 1].id);
          }
        } else if (event.key === "ArrowLeft") {
          if (currentIndex > 0) {
            setActiveImage(imageList[currentIndex - 1].id);
          }
        } else if (event.key === "ArrowRight") {
          if (currentIndex < imageList.length - 1) {
            setActiveImage(imageList[currentIndex + 1].id);
          }
        } else if (event.key === "Escape") {
          handleCloseClick();
        }
      } else if (activeImage === null) {
        // Thumbnail navigation only when no image is expanded
        // Focus is not on the overlay (it's on the thumbnails)
        const gridItems = Array.from(document.querySelectorAll(".grid .item"));
        let itemsPerRow = 1;
        for (let i = 1; i < gridItems.length; i++) {
          if (gridItems[i].offsetTop !== gridItems[i - 1].offsetTop) {
            break;
          }
          itemsPerRow++;
        }

        if (event.key === "ArrowLeft") {
          const prevElement = focusedElement.previousSibling;
          if (prevElement) {
            prevElement.focus();
          }
        } else if (event.key === "ArrowRight") {
          const nextElement = focusedElement.nextSibling;
          if (nextElement) {
            nextElement.focus();
          }
        } else if (event.key === "ArrowUp") {
          const currentIdx = gridItems.indexOf(focusedElement);
          const targetIdx = currentIdx - itemsPerRow;
          if (targetIdx >= 0) {
            gridItems[targetIdx].focus();
          }
        } else if (event.key === "ArrowDown") {
          const currentIdx = gridItems.indexOf(focusedElement);
          const targetIdx = currentIdx + itemsPerRow;
          if (targetIdx < gridItems.length) {
            gridItems[targetIdx].focus();
          }
        }
      }
    },
    [activeImage, handleCloseClick, imageList, lastKeyTime]
  );

  useEffect(() => {
    const checkImagesOnScroll = () => {
      imageList.forEach((image) => {
        if (!image.loaded && isInViewport(image.id)) {
          loadImage(image.id);
        }
      });
    };

    window.addEventListener("scroll", checkImagesOnScroll);
    return () => window.removeEventListener("scroll", checkImagesOnScroll);
  }, [imageList]);

  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [handleKeyDown]);

  useEffect(() => {
    const checkImagesOnScroll = () => {
      imageList.forEach((image) => {
        if (!image.loaded && isInViewport(image.id)) {
          loadImage(image.id);
        }
      });
    };

    window.addEventListener("scroll", checkImagesOnScroll);
    return () => window.removeEventListener("scroll", checkImagesOnScroll);
  }, [imageList]);

  useEffect(() => {
    const isTouchDevice =
      "ontouchstart" in window ||
      navigator.maxTouchPoints > 0 ||
      navigator.msMaxTouchPoints > 0;
    if (activeImage !== null && !instructionsShown && isTouchDevice) {
      setShowSwipeText(true);
      const timeout = setTimeout(() => {
        setShowSwipeText(false);
        setInstructionsShown(true);
      }, 5000); // Hide after 5 seconds
      return () => clearTimeout(timeout);
    }
    const handleClickOutside = (event) => {
      if (event.target.classList.contains("arrow")) {
        return;
      }
      if (activeImage !== null) {
        handleCloseClick();
      }
    };
    window.addEventListener("mousedown", handleClickOutside);
    return () => {
      window.removeEventListener("mousedown", handleClickOutside);
    };
  }, [activeImage, handleCloseClick, instructionsShown]);

  const handleWheel = useCallback(
    (event) => {
      if (activeImage === null) return;
      const currentIndex = imageList.findIndex(({ id }) => id === activeImage);
      if (event.deltaY > 0 && currentIndex < imageList.length - 1) {
        setActiveImage(imageList[currentIndex + 1].id);
      } else if (event.deltaY < 0 && currentIndex > 0) {
        setActiveImage(imageList[currentIndex - 1].id);
      }
    },
    [activeImage, imageList]
  );

  useEffect(() => {
    const checkImagesOnScroll = () => {
      imageList.forEach((image) => {
        if (!image.loaded && isInViewport(image.id)) {
          loadImage(image.id);
        }
      });
    };

    window.addEventListener("scroll", checkImagesOnScroll);
    return () => window.removeEventListener("scroll", checkImagesOnScroll);
  }, [imageList]);

  useEffect(() => {
    if (activeImage !== null) {
      window.addEventListener("wheel", handleWheel);
    }
    return () => {
      window.removeEventListener("wheel", handleWheel);
    };
  }, [handleWheel, activeImage]);

  if (!galleryExists) {
    return null;
  }

  const currentIndex = imageList.findIndex(({ id }) => id === activeImage);

  if (!galleryExists || imageList.length === 0) {
    // You could return a placeholder or a message here
    return null;
  }

  return (
    <section className="gallery">
      {isLoading ? (
        <div className="observer_spinner"></div> // Show spinner while loading
      ) : (
        <>
          {showSwipeText && !instructionsShown && (
            <div className="swipe-gallery">
              <ion-icon
                name="return-up-back-outline"
                role="img"
                aria-label="return-up-back"
                className="md hydrated"
              ></ion-icon>
              Swipe left or right
              <ion-icon
                name="return-up-forward-outline"
                role="img"
                aria-label="return-up-forward"
                className="md hydrated"
              ></ion-icon>
            </div>
          )}
          <div id="grid" className="grid">
            {imageList.map(({ id, details, imageUrl, className }, index) => (
              <Animations type="fadeInUp" key={`anim${id}`}>
                <div
                  id={`item${id}`}
                  className={`item ${className} ${
                    activeImage === id ? "item--active" : ""
                  }`}
                  key={id}
                  style={{
                    backgroundImage: `url(${imageUrl})`,
                    pointerEvents: activeImage !== null ? "none" : "auto",
                  }}
                  onClick={(event) => handleImageClick(id, event)}
                  onKeyDown={(event) => handleThumbnailKeyDown(id, event)}
                  tabIndex="0"
                  role="button"
                  aria-label={`Thumbnail for image ${id}`}
                >
                  <div className="item__link"></div>
                  <div className="item__details" data-id={id}>
                    {details}
                  </div>
                </div>
              </Animations>
            ))}
          </div>
          {activeImage !== null && (
            <>
              {currentIndex > 0 && (
                <div
                  className="arrow arrow--left"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleArrowClick("left");
                  }}
                >
                  <ion-icon
                    name="arrow-back-outline"
                    role="img"
                    aria-label="arrow left"
                    className="md hydrated"
                  ></ion-icon>
                </div>
              )}
              {currentIndex < imageList.length - 1 && (
                <div
                  className="arrow arrow--right"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleArrowClick("right");
                  }}
                >
                  <ion-icon
                    name="arrow-forward-outline"
                    role="img"
                    aria-label="arrow right"
                    className="md hydrated"
                  ></ion-icon>
                </div>
              )}
              <div
                className="overlay"
                onTouchStart={handleTouchStart}
                onTouchMove={(event) => {
                  if (activeImage !== null && event.cancelable) {
                    event.preventDefault();
                  }
                  handleTouchMove(event);
                }}
                onTouchEnd={handleTouchEnd}
                onClick={handleCloseClick}
                style={{
                  zIndex: 1000,
                  position: "fixed",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                }}
                tabIndex="0"
                onKeyDown={handleKeyDown}
              ></div>
              <div className="active-image-details">
                <p>{`${currentIndex + 1}/${imageList.length} - ${
                  imageList[currentIndex].details
                }`}</p>
              </div>
            </>
          )}
        </>
      )}
    </section>
  );
};

export default Gallery;
